import * as React from 'react';

import { cn } from '../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  autoGrow?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, autoGrow, onChange, rows = 4, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const handleInput = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autoGrow && innerRef.current) {
          innerRef.current.style.height = 'auto';
          innerRef.current.style.height = `${innerRef.current.scrollHeight}px`;
        }
        onChange?.(e);
      },
      [autoGrow, onChange],
    );

    return (
      <textarea
        ref={innerRef}
        rows={rows}
        onChange={handleInput}
        aria-invalid={invalid || undefined}
        className={cn(
          'block w-full rounded-[var(--radius-md)] border bg-[var(--color-surface)] px-3 py-2 text-sm',
          'text-[var(--color-fg)] placeholder:text-[var(--color-fg-muted)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-bg)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          invalid
            ? 'border-[var(--color-danger)] focus-visible:ring-[var(--color-danger)]'
            : 'border-[var(--color-border)] focus-visible:border-[var(--color-accent)]',
          autoGrow && 'resize-none overflow-hidden',
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';
